// Pure rollup derive helpers — Task→Project→Goal vertical linking.
// D1: progressMode 'auto' (default) | 'manual'.
// D2: Project.goalId links project to goal (1-way, no recursion).
// D29: completedAt set by action layer, not here.
import type { Goal, LifeOsState } from "../types";
import { autoKrCurrent, goalProgress, isAdvancedGoal } from "./okr-derive";

/**
 * Derive display progress for a project.
 * - manual mode → stored project.progress
 * - auto mode (default) → % tasks done, rounded (0 tasks → fallback project.progress)
 */
export function deriveProjectProgress(state: LifeOsState, projectId: string): number {
  const project = state.projects.find((p) => p.id === projectId);
  if (!project) return 0;
  if (project.progressMode === "manual") return project.progress;
  // Auto: count tasks belonging to this project
  const linked = state.tasks.filter((t) => t.projectId === projectId);
  if (linked.length === 0) return project.progress; // fallback: stored progress (avoids 0/0)
  const done = linked.filter((t) => t.status === "done").length;
  return Math.round((done / linked.length) * 100);
}

/** Done/total task counts for a project — used for the "x/y việc" label beside auto progress. */
export function deriveProjectTaskCounts(state: LifeOsState, projectId: string): { done: number; total: number } {
  const linked = state.tasks.filter((t) => t.projectId === projectId);
  return { done: linked.filter((t) => t.status === "done").length, total: linked.length };
}

export type GoalRollupResult = {
  /** Derived current value for the goal (average of child project progress or stored) */
  current: number;
  /** IDs of projects linked to this goal via Project.goalId */
  linkedProjectIds: string[];
  /** Average project progress across linked projects (0 if none) */
  projectProgressAvg: number;
};

/**
 * Derive rollup for a goal.
 * - Finds all projects where Project.goalId === goal.id (D2)
 * - auto mode (default, i.e. progressMode !== 'manual'):
 *     current = average of deriveProjectProgress() across linked projects
 *     (0 if no linked projects)
 * - manual mode: keeps stored goal.current unchanged
 */
export function deriveGoalRollup(state: LifeOsState, goalId: string): GoalRollupResult {
  const goal = state.goals.find((g) => g.id === goalId);
  if (!goal) return { current: 0, linkedProjectIds: [], projectProgressAvg: 0 };

  const linkedProjects = state.projects.filter((p) => p.goalId === goalId);
  const linkedProjectIds = linkedProjects.map((p) => p.id);

  const projectProgressAvg =
    linkedProjects.length === 0
      ? 0
      : Math.round(
          linkedProjects.reduce((sum, p) => sum + deriveProjectProgress(state, p.id), 0) /
            linkedProjects.length,
        );

  if (goal.progressMode === "manual") {
    return { current: goal.current, linkedProjectIds, projectProgressAvg };
  }

  // Auto: current = avg project progress; 0 if no linked projects
  const current = linkedProjects.length === 0 ? 0 : projectProgressAvg;
  return { current, linkedProjectIds, projectProgressAvg };
}

/**
 * CANONICAL goal progress % — dùng CHUNG cho mọi nơi hiển thị (card, detail,
 * dashboard) để không còn 3 con số khác nhau:
 *  - Advanced (có KR) → trung bình % các KR.
 *  - Simple/manual    → current/target (current = manual?goal.current : TB dự án).
 * Đây chính là logic vốn nội tuyến ở goals-view/goal-detail, gom lại 1 chỗ.
 */
/**
 * P5-2a: return the goal with any auto KRs' `current` resolved live from their
 * linked project's task completion. Non-auto KRs pass through unchanged.
 */
export function resolveAutoKrs(state: LifeOsState, goal: Goal): Goal {
  if (!Array.isArray(goal.keyResults)) return goal;
  return {
    ...goal,
    keyResults: goal.keyResults.map((kr) =>
      kr.auto && kr.linkedProjectId
        ? { ...kr, current: autoKrCurrent(kr.target, deriveProjectProgress(state, kr.linkedProjectId)) }
        : kr,
    ),
  };
}

export function goalDisplayPercent(state: LifeOsState, goal: Goal): number {
  if (isAdvancedGoal(goal)) return goalProgress(resolveAutoKrs(state, goal)); // TB% các KR
  if (goal.progressMode === "manual") {
    return Math.min(100, Math.round((goal.current / Math.max(goal.target, 1)) * 100));
  }
  // Auto: tiến độ = TB% tiến độ các dự án liên kết (đã là %), 0 nếu chưa link.
  return deriveGoalRollup(state, goal.id).projectProgressAvg;
}
